import {ActionTree, GetterTree, Module, MutationTree} from "vuex";
import mapboxgl from "mapboxgl"; // Ensure mapboxgl types are imported
import {Modal} from "bootstrap"; // Import Bootstrap Modal type/class
import graph_client from "../../../../utils/graphql/graph_client";
import {
  CreateWaypoint,
  CreateWaypointGroup,
  CreateWaypoints,
  DestroyWaypoint,
  DestroyWaypointGroup,
  DestroyWaypointImage,
  GetAllUserWaypointGroups,
  GetAllWaypointsForUser,
  GetWaypointByIdOrClientId,
  GetWaypointGroupByIdOrClientId,
  UpdateWaypoint,
  UpdateWaypointGroup,
  WaypointsSearch
} from "../../../../utils/graphql/waypoints-graph";
import {WaypointOptionsDialogPages} from "../../../../utils/models/WaypointOptionsDialogPages";
import {wktPointToLatLng} from "../../../../utils/helpers/WKTPointToLatLng";
import {
  waypointsToGeoJsonFeatureCollectionHelper
} from "../../../../utils/helpers/WaypointsToGeoJsonFeatureCollectionHelper";
import {POI, POIUtility} from "../../../../utils/models/POI";
import {isClickOnWaypointHelper} from "../../../../utils/helpers/IsClickOnWaypointHelper";
import {storage, ref, uploadBytesResumable, getDownloadURL} from '../../../../utils/firebase/storage';
import {getWaypointClientIdFromUrl} from "../../../../utils/helpers/GetWaypointClientIdFromUrl";
import {cleanseUrl} from "../../../../utils/helpers/CleanseAndPushUrl";
import {generateRandomInt} from "../../../../utils/helpers/IDGenerator";
import {createNewWaypointJsonHelper} from "../../../../utils/helpers/CreateNewWaypointJsonHelper";
import {createWaypointMapLayerHelper} from "../../../../utils/helpers/CreateWaypointMapLayerHelper";
import {gpxFileToGeoJsonHelper} from "../../../../utils/helpers/GpxFileToGeoJsonHelper";
import {
  getBoundsFromGeoJsonPointsFeatureCollection
} from "../../../../utils/helpers/GetBoundsFromGeoJsonPointsFeatureCollection";
// Import the helper that adds individual waypoints AND their listeners
import {addWaypointToMapHelper} from "../../../../utils/helpers/AddWaypointToMapHelper";
import {geojsonToGpx} from "../../../../utils/togpx/geojsonToGpx";
import {MapLayerCategory} from "../../../../utils/layer-registry";

// Define the interface for the state shape
export interface WaypointDialogState {
  waypointAddingModeEnabled: boolean;
  pageStack: Array<WaypointOptionsDialogPages>;
  allUserWaypoints: Array<any>;
  allUserWaypointGroups: Array<any>;
  searchedWaypoints: Array<any>;
  selectedWaypointGroup: any;
  mapContainsWaypoints: boolean; // Tracks if the main 'waypoints' layer is present
  selectedWaypoint: any;
  waypointsReadyForUpload: Array<any>;
  waypointPhotosAwaitingUpload: Array<any>;
  waypointPhotosToRemove: Array<any>;
  loading: boolean;
  individualWaypointLayerIds: Array<string>; // Tracks IDs from addWaypointToMapHelper

  // Store the ID used for the main waypoint layer
  waypointLayerId: string;
}

// Define the RootState interface if you have one, otherwise use 'any'
type RootState = any;

const state = (): WaypointDialogState => ({
  waypointAddingModeEnabled: false,
  pageStack: [WaypointOptionsDialogPages.WaypointOptions],
  allUserWaypoints: [],
  allUserWaypointGroups: [],
  searchedWaypoints: [],
  selectedWaypointGroup: null,
  mapContainsWaypoints: false,
  selectedWaypoint: null,
  waypointsReadyForUpload: [],
  waypointPhotosAwaitingUpload: [],
  waypointPhotosToRemove: [],
  loading: false,
  individualWaypointLayerIds: [],
  waypointLayerId: 'waypoints', // Default/expected ID for the main layer
});

const actions: ActionTree<WaypointDialogState, RootState> = {
  initialiseStore(context: any, payload: any) {
    const dialogElement = document.getElementById('waypoint-options-dialog');
    if (dialogElement) {
      // Use jQuery or direct Bootstrap instance as needed
      // Example using jQuery if still present:
      const dialog = $('#waypoint-options-dialog');
      dialog.on('hidden.bs.modal', () => {
        context.commit('resetPageStack');
        context.commit('clearSearchedWaypoints');
        context.commit('resetUploadAndRemoveLists');
      });
      // If using Bootstrap JS directly:
      // dialogElement.addEventListener('hidden.bs.modal', () => { ... });
    }

    // Ensure window.map exists and is initialized before adding listeners
    if (window.map) {
      const map = window.map as mapboxgl.Map; // Use typed map instance

      // --- Style Load Handler ---
      map.on('style.load', () => {
        console.log('Map style loaded (style.load handler)');
        // Mapbox removes layers/sources on style change.
        // Reset state tracking layers managed by this module.
        const wasMapContainingWaypoints = context.state.mapContainsWaypoints;
        context.commit('setMapContainsWaypoints', false); // Assume main layer is gone
        context.commit('clearIndividualWaypointLayerIds'); // Assume individual layers are gone

        // Re-add the main layer if it was supposed to be there before the style change
        if (wasMapContainingWaypoints) {
          console.log('Re-adding main waypoints layer after style load.');
          // Debounce or delay slightly? Sometimes style.load fires before everything is truly ready.
          // setTimeout(() => { context.dispatch('addWaypointsToMap'); }, 100); // Optional delay
          context.dispatch('addWaypointsToMap'); // Re-adds the layer and its listeners
        }
        // Individual waypoints added via addWaypointToMapHelper are typically lost
        // on style change unless the app has separate logic to re-add them based on state.
      });

      // --- Click Handler ---
      map.on('click', (e: mapboxgl.MapMouseEvent & { point: mapboxgl.Point } ) => {
        // Define all potential waypoint layer IDs
        const allWaypointLayerIds = [
          context.state.waypointLayerId, // Main layer ID
          ...context.state.individualWaypointLayerIds // All individual layer IDs
        ];
        // Filter out IDs for layers that might not actually exist at the moment
        const existingWaypointLayerIds = allWaypointLayerIds.filter(id => map.getLayer(id));

        // Query only the existing waypoint layers
        const features = map.queryRenderedFeatures(e.point, {
          layers: existingWaypointLayerIds
        });

        // Check if the click was on one of *these* features
        if (features.length > 0 && isClickOnWaypointHelper(features)) {
          const clickedFeature = features[0];
          // Prefer clientId from properties if available, fallback to feature id (which might be db id or client id depending on source)
          const clickedClientId = clickedFeature.properties?.clientId || clickedFeature.id;
          if (clickedClientId) {
            console.log('Clicked on waypoint feature:', clickedClientId);
            context.dispatch('getWaypointFrom', clickedClientId);
          } else {
            console.warn("Clicked waypoint feature missing usable clientId/id property.", clickedFeature);
          }
          return; // Prevent falling through to waypoint adding mode
        }

        // Handle waypoint adding mode if no waypoint was clicked
        if (context.state.waypointAddingModeEnabled) {
          context.dispatch('createWaypoint', { lngLat: e.lngLat });
        }
      });

      // --- Map Load Handler (for deep links, etc.) ---
      map.on('load', () => {
        console.log('Map load event triggered.');
        const waypointClientId = getWaypointClientIdFromUrl();
        // Ensure selectedWaypoint state is potentially loaded *before* map.load fires if coming from deep link
        if (waypointClientId && context.state.selectedWaypoint) {
          console.log('Handling deep link for waypoint:', waypointClientId);
          const latLng = wktPointToLatLng(context.state.selectedWaypoint.lonlat);
          if (latLng) {
            let poiType = POIUtility.fromString(context.state.selectedWaypoint.type);
            // Use the helper which adds the waypoint AND its listeners
            const addedWaypointLayerId = addWaypointToMapHelper(
              poiType, waypointClientId, latLng
            );
            if (addedWaypointLayerId) {
              context.commit('addIndividualWaypointLayerId', addedWaypointLayerId); // Track it
              const bounds = getBoundsFromGeoJsonPointsFeatureCollection(
                waypointsToGeoJsonFeatureCollectionHelper([context.state.selectedWaypoint])
              );
              if (bounds) {
                const convertedBounds: mapboxgl.LngLatBoundsLike = [
                  [bounds.southwest[1], bounds.southwest[0]],  // [minLon, minLat]
                  [bounds.northeast[1], bounds.northeast[0]]   // [maxLon, maxLat]
                ];
                map.fitBounds(convertedBounds, { padding: 50, maxZoom: 15 }); // Increased padding
              }
            }
          } else {
            console.error("Could not parse WKT for deep link waypoint:", context.state.selectedWaypoint.lonlat);
          }
        } else if (waypointClientId && !context.state.selectedWaypoint) {
          console.warn("Deep link detected, but selectedWaypoint not yet loaded.");
          // The GetWaypointByIdOrClientId request below should handle adding it once loaded.
        }
      });

    } else {
      console.warn("window.map not initialized when initialiseStore was called in WaypointDialogModule.");
    }

    // --- Deep Link Waypoint Fetching ---
    const waypointClientId = getWaypointClientIdFromUrl();
    if (waypointClientId) {
      console.log("Attempting to fetch deep linked waypoint:", waypointClientId);
      graph_client.request(GetWaypointByIdOrClientId, { "clientId": waypointClientId }).then((data: any) => {
        if (data.waypointByIdOrClientId) {
          context.commit('setSelectedWaypoint', data.waypointByIdOrClientId); // Use mutation
          context.dispatch('showViewWaypointDetails'); // Show details (may also trigger modal)

          // If map is already loaded when data arrives, add waypoint to map
          if (window.map && window.map.isStyleLoaded()) {
            console.log('Map ready, adding deep linked waypoint now.');
            const latLng = wktPointToLatLng(context.state.selectedWaypoint.lonlat);
            if (latLng) {
              let poiType = POIUtility.fromString(context.state.selectedWaypoint.type);
              // Use the helper which adds the waypoint AND its listeners
              const addedWaypointLayerId = addWaypointToMapHelper(
                poiType, waypointClientId, latLng
              );
              if (addedWaypointLayerId) {
                context.commit('addIndividualWaypointLayerId', addedWaypointLayerId); // Track it
                const bounds = getBoundsFromGeoJsonPointsFeatureCollection(
                  waypointsToGeoJsonFeatureCollectionHelper([context.state.selectedWaypoint])
                );
                if (bounds) {
                  const convertedBounds: mapboxgl.LngLatBoundsLike = [
                    [bounds.southwest[1], bounds.southwest[0]],  // [minLon, minLat]
                    [bounds.northeast[1], bounds.northeast[0]]   // [maxLon, maxLat]
                  ];
                  window.map.fitBounds(convertedBounds, { padding: 50, maxZoom: 15 });
                }
              }
            } else {
              console.error("Could not parse WKT for fetched deep link waypoint:", context.state.selectedWaypoint.lonlat);
            }
          } // Else, the 'load' listener above should handle adding it once map is ready
        } else {
          console.error("Deep linked waypoint not found:", waypointClientId);
          alert("Waypoint not found");
          cleanseUrl(window.location.href);
        }
      }).catch(error => {
        console.error("Error fetching deep linked waypoint by client ID:", error);
        alert("Error finding waypoint.");
        cleanseUrl(window.location.href);
      });
    }

    // --- Add Marker From Popup Integration ---
    // Ensure this is only attached once if initialiseStore can be called multiple times
    if (!window.addMarkerClickedFromPopup) {
      // @ts-ignore - Attaching to window is generally discouraged, consider event bus
      window.addMarkerClickedFromPopup = (lngLat: mapboxgl.LngLatLike) => {
        context.dispatch('createWaypoint', { lngLat: lngLat });
        // Handle removal of temporary mapbox popup marker if needed elsewhere
        console.warn("window.addMarkerClickedFromPopup called. Ensure any temporary map markers are managed.");
      };
    }
  },

  getWaypointFrom(context: any, clientId: any) {
    graph_client.request(GetWaypointByIdOrClientId, { clientId }).then((data: any) => {
      if (data.waypointByIdOrClientId) {
        context.commit('setSelectedWaypoint', data.waypointByIdOrClientId);
        context.dispatch('showViewWaypointDetails');
      } else {
        console.error("Waypoint not found for clientId:", clientId);
        alert("Could not load waypoint details.");
      }
    }).catch(error => console.error("Error fetching waypoint:", error));
  },

  refreshSelectedWaypoint(context: any) {
    if (!context.state.selectedWaypoint?.clientId) return;
    graph_client.request(GetWaypointByIdOrClientId, { clientId: context.state.selectedWaypoint.clientId }).then((data: any) => {
      context.commit('setSelectedWaypoint', data.waypointByIdOrClientId);
    }).catch(error => console.error("Error refreshing selected waypoint:", error));
  },

  getAllUserWaypoints(context: any, payload: any) {
    context.commit('setLoading', true);
    graph_client.request(GetAllWaypointsForUser).then((data: any) => {
      context.commit('setAllUserWaypoints', data.waypoints);
    }).catch(error => {
      console.error("Error fetching user waypoints:", error);
    }).finally(() => {
      context.commit('setLoading', false);
    });
  },

  deleteSelectedWaypoint(context: any) {
    if (!context.state.selectedWaypoint?.id) return;
    const waypointTitle = context.state.selectedWaypoint.title; // Get title for confirmation/logging
    if (!confirm(`Are you sure you want to delete waypoint "${waypointTitle}"?`)) {
      return;
    }
    context.commit('setLoading', true);
    graph_client.request(DestroyWaypoint, { waypoint: { id: context.state.selectedWaypoint.id } }).then(async (data: any) => {
      await context.dispatch('getAllUserWaypoints'); // Refresh list

      // If the main layer is showing, refresh it
      if (context.state.mapContainsWaypoints) {
        await context.dispatch('addWaypointsToMap'); // Re-adds the layer with updated data
      }

      // Hide modal
      const element = document.getElementById('waypoint-options-dialog');
      if (element) {
        const modal = Modal.getInstance(element) || new Modal(element);
        modal.hide();
      }

      context.commit('setSelectedWaypoint', null); // Clear selected waypoint
      context.commit('resetPageStack'); // Go back to default view
      console.log(`Waypoint "${waypointTitle}" deleted.`);

    }).catch((error: any) => {
      console.error("Error deleting waypoint:", error);
      alert("Error deleting waypoint. Please try again.");
    }).finally(() => {
      context.commit('setLoading', false);
    });
  },

  async deleteWaypoint(context, waypoint) { // Used for deleting from lists maybe?
    if (!waypoint?.id) return;
    if (!confirm(`Are you sure you want to delete waypoint "${waypoint.title || 'this waypoint'}"?`)) {
      return;
    }
    context.commit('setLoading', true);
    try {
      await graph_client.request(DestroyWaypoint, { waypoint: { id: waypoint.id } });
      await context.dispatch('getAllUserWaypoints'); // Refresh the list
      if (context.state.mapContainsWaypoints) {
        await context.dispatch('addWaypointsToMap'); // Re-adds the layer with updated data
      }
    } catch (e) {
      console.error("Error deleting waypoint:", e);
      alert("Failed to delete waypoint.");
    } finally {
      context.commit('setLoading', false);
    }
  },

  showViewWaypointDetails(context: any) {
    if (context.state.selectedWaypoint == null) {
      console.error("Attempted to show details with no waypoint selected");
      return;
    }
    context.commit('setTopPage', WaypointOptionsDialogPages.WaypointViewDetails);
    const element = document.getElementById('waypoint-options-dialog');
    if (element) {
      // Get existing instance or create new one
      const modal = Modal.getInstance(element) || new Modal(element);
      modal.show();
    } else {
      console.error("Waypoint options dialog element not found.");
    }
  },

  getAllUserWaypointGroups(context: any) {
    context.commit('setLoading', true);
    graph_client.request(GetAllUserWaypointGroups).then((data: any) => {
      context.commit('setAllUserWaypointGroups', data.waypointGroups);
    }).catch(error => {
      console.error("Error fetching user waypoint groups:", error);
    }).finally(() => {
      context.commit('setLoading', false);
    });
  },

  updateWaypointGroup(context: any, payload: { id: string, title: string, waypointIds: number[] }) {
    context.commit('setLoading', true);
    graph_client.request(UpdateWaypointGroup, { waypointGroup: payload }).then(async (data: any) => {
      await context.dispatch('getAllUserWaypointGroups'); // Refresh the list
      // Refresh the selected group if it's the one being edited
      if(context.state.selectedWaypointGroup?.id === payload.id && context.state.selectedWaypointGroup?.clientId) {
        context.dispatch('getWaypointGroup', context.state.selectedWaypointGroup.clientId);
      }
    }).catch(error => {
      console.error("Error updating waypoint group:", error);
      alert("Failed to update group.");
    }).finally(() => {
      context.commit('setLoading', false);
    });
  },

  getWaypointGroup(context: any, clientId: string) {
    context.commit('setLoading', true);
    graph_client.request(GetWaypointGroupByIdOrClientId, { clientId: clientId }).then((data: any) => {
      context.commit('setSelectedWaypointGroup', data.waypointGroupByIdOrClientId);
    }).catch(error => {
      console.error("Error fetching waypoint group:", error);
      context.commit('setSelectedWaypointGroup', null); // Clear if error
    }).finally(() => {
      context.commit('setLoading', false);
    });
  },

  createWaypointGroup(context: any, payload: { title: string, waypointIds: number[] }) {
    context.commit('setLoading', true);
    graph_client.request(CreateWaypointGroup, { waypointGroup: payload }).then((data: any) => {
      context.dispatch('getAllUserWaypointGroups'); // Refresh the list
    }).catch((error: any) => {
      console.error("Error creating waypoint group:", error);
      alert("Failed to create group.");
    }).finally(() => {
      context.commit('setLoading', false);
    });
  },

  async createWaypoint(context: any, payload: { lngLat: mapboxgl.LngLat, title?: string, type?: POI, description?: string }) {
    const title = payload.title ?? "Custom Waypoint";
    const type = payload.type ?? POI.Other;
    const description = payload.description ?? "";
    context.commit('setLoading', true);
    try {
      const response = await graph_client.request(CreateWaypoint, {
        "waypoint": createNewWaypointJsonHelper(payload.lngLat.lng, payload.lngLat.lat, title, type, description)
      });
      const createdWaypoint = response?.createWaypoint;

      if (createdWaypoint?.clientId && window.map?.isStyleLoaded()) {
        // Use the helper which adds the waypoint AND its listeners
        const addedWaypointLayerId = addWaypointToMapHelper(
          POIUtility.fromString(createdWaypoint.type),
          createdWaypoint.clientId,
          payload.lngLat // Pass LngLat object directly
        );
        if (addedWaypointLayerId) {
          context.commit('addIndividualWaypointLayerId', addedWaypointLayerId); // Track it
        } else {
          console.error("addWaypointToMapHelper failed to return a layer ID.");
        }
        // Optionally refresh the main waypoints data/layer if desired
        // await context.dispatch('getAllUserWaypoints');
        // if (context.state.mapContainsWaypoints) await context.dispatch('addWaypointsToMap');

      } else {
        console.warn("Waypoint created but could not be added to map immediately.", response);
        // Fetch all waypoints so the new one is included in data, even if not shown individually
        await context.dispatch('getAllUserWaypoints');
      }
    } catch (error) {
      console.error("Error creating waypoint:", error);
      alert("Failed to create waypoint.");
    } finally {
      context.commit('setLoading', false);
    }
  },

  searchWaypoints(context: any, searchString: string) {
    context.commit('setLoading', true);
    graph_client.request(WaypointsSearch, { 'searchString': searchString }).then((data: any) => {
      context.commit('setSearchedWaypoints', data.waypointsSearch);
    }).catch(error => {
      console.error("Error searching waypoints:", error);
      context.commit('setSearchedWaypoints', []); // Clear on error
    }).finally(() => {
      context.commit('setLoading', false);
    });
  },

  removeAllIndividualWaypointsFromMap(context: any) {
    if (!window.map) return;
    const map = window.map as mapboxgl.Map;

    context.state.individualWaypointLayerIds.forEach(layerId => {
      try {
        // Remove listeners for this specific layer ID
        map.off('mouseenter', layerId);
        map.off('mouseleave', layerId);

        if (map.getLayer(layerId)) map.removeLayer(layerId);
        // Source ID should match layer ID based on addWaypointToMapHelper logic
        if (map.getSource(layerId)) map.removeSource(layerId);

      } catch (e) {
        // Ignore errors if layer/source already removed
        if (!e.message.includes('does not exist')) { // Avoid spamming console for expected removals
          console.warn(`Error removing individual waypoint layer/source ${layerId}:`, e);
        }
      }
    });

    // Reset cursor after attempting removal of all relevant layers
    if (map.getCanvas()) map.getCanvas().style.cursor = '';

    context.commit('clearIndividualWaypointLayerIds'); // Clear the tracking array
  },

  addWaypointsToMap(context: any) { // For the main layer
    if (!window.map) {
      console.warn("addWaypointsToMap called but map is not available.");
      return;
    }
    const map = window.map as mapboxgl.Map;
    context.commit('setLoading', true);

    // Fetch fresh data first
    graph_client.request(GetAllWaypointsForUser).then((data: any) => {
      context.commit('setAllUserWaypoints', data.waypoints); // Update state with fresh data
      const geojson = waypointsToGeoJsonFeatureCollectionHelper(context.state.allUserWaypoints); // Use updated state

      const addLayerAndSource = () => {
        // Check map still exists, e.g. user hasn't navigated away quickly
        if (!window.map) return;
        const map = window.map as mapboxgl.Map;

        try {
          const sourceId = "waypoints";
          const layerId = context.state.waypointLayerId;

          // --- Cleanup potential existing layer/source/listeners ---
          map.off('mouseenter', layerId); // Remove previous listener if any
          map.off('mouseleave', layerId); // Remove previous listener if any

          const layerConfig = createWaypointMapLayerHelper(layerId, sourceId);
          window.layerRegistry.addSource(sourceId, { type: "geojson", data: geojson }, true); // Add the source to the map
          window.layerRegistry.addLayer(layerConfig, MapLayerCategory.Top); // Register the layer

          // --- Add Listeners ---
          map.on('mouseenter', layerId, () => { if (map.getCanvas()) map.getCanvas().style.cursor = 'pointer'; });
          map.on('mouseleave', layerId, () => { if (map.getCanvas()) map.getCanvas().style.cursor = ''; });
          // --- End Listeners ---

          context.commit('setMapContainsWaypoints', true);
        } catch (e) {
          console.error("Error adding main waypoints layer/source:", e);
          context.commit('setMapContainsWaypoints', false); // Ensure state reflects reality
        } finally {
          context.commit('setLoading', false); // Stop loading indicator
        }
      };

      // Ensure map style is loaded before adding layer/source
      if (map.isStyleLoaded()) {
        addLayerAndSource();
      } else {
        console.log("Map style not loaded, waiting for 'style.load' to add main waypoints layer.");
        map.once('style.load', addLayerAndSource);
      }

    }).catch(error => {
      console.error("Error fetching waypoints before adding to map:", error);
      context.commit('setLoading', false); // Stop loading on error
    });
  },

  removeWaypointsFromMap(context: any) { // For the main layer
    if (!window.map) return;
    const map = window.map as mapboxgl.Map;
    const layerId = context.state.waypointLayerId;
    const sourceId = "waypoints";

    try {
      // Remove listeners first
      map.off('mouseenter', layerId);
      map.off('mouseleave', layerId);

      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);

      // Reset cursor just in case it was left as 'pointer'
      if (map.getCanvas()) map.getCanvas().style.cursor = '';

      context.commit('setMapContainsWaypoints', false);
    } catch (e) {
      // Ignore errors if layer/source already removed
      if (!e.message.includes('does not exist')) { // Avoid spamming console for expected removals
        console.warn(`Error removing main waypoints layer/source:`, e);
      }
      // Ensure state is false even if removal failed partially
      context.commit('setMapContainsWaypoints', false);
    }
  },

  async uploadWaypointImages(context: any, files: File[]) {
    if (!context.state.selectedWaypoint) {
      alert("No waypoint selected for image upload.");
      return;
    }
    context.commit('setLoading', true);
    context.commit('addWaypointPhotosAwaitingUpload', files);
    for (const file of files) {
      try {
        const fileRef = ref(storage, `Public_Images/${Date.now()}_${file.name}`);
        await uploadBytesResumable(fileRef, file)
        const downloadURL = await getDownloadURL(fileRef)
        const tempPhotoObject = { id: `temp_${generateRandomInt()}`, file: { url: downloadURL }, __typename: 'WaypointPhoto' };
        context.commit('addPhotoToSelectedWaypoint', tempPhotoObject);
        context.commit('addWaypointReadyForUpload', downloadURL);
      } catch (e) {
        console.error(`Error uploading file ${file.name}:`, e);
        alert(`Failed to upload ${file.name}.`);
        context.commit('removeWaypointPhotoAwaitingUpload', file); // Remove from awaiting list on error
        break; // Stop further uploads on error
      } finally {
        context.commit('removeWaypointPhotoAwaitingUpload', file);
      }
    }

    console.log("All image uploads finished or failed.");

    context.commit('setLoading', false);
  },

  saveSelectedWaypoint(context: any) {
    if (!context.state.selectedWaypoint?.id) {
      alert("No waypoint selected to save.");
      return;
    }
    if (context.state.waypointPhotosAwaitingUpload.length > 0) {
      alert("Please wait for all images to finish uploading before saving.");
      return;
    }
    context.commit('setLoading', true);

    const removalPromises = context.state.waypointPhotosToRemove.map(photo => {
      if (photo?.id && !String(photo.id).startsWith('temp_')) {
        return context.dispatch('removeWaypointImage', photo).catch(e => {
          console.error(`Failed to remove image ID ${photo.id}:`, e);
          // Decide how to handle failure - maybe alert user, maybe proceed anyway
          alert(`Failed to remove one or more images. Please check and try saving again.`);
          throw new Error("Image removal failed"); // Throw to stop the save process
        });
      }
      return Promise.resolve();
    });

    Promise.all(removalPromises).then(() => {
      // If removals succeeded (or none were needed), proceed to update waypoint
      const existingPhotoUrls = context.state.selectedWaypoint.waypointPhotos
        ?.filter((photo: any) =>
          !context.state.waypointPhotosToRemove.some((pToRemove: any) => pToRemove.id === photo.id)
          && photo.file?.url
        )
        .map((photo: any) => photo.file.url) || []; // Handle case where waypointPhotos is null/undefined

      const uniqueFinalUrls = new Set([...existingPhotoUrls, ...context.state.waypointsReadyForUpload]);

      const waypointData = {
        id: context.state.selectedWaypoint.id,
        lonlat: context.state.selectedWaypoint.lonlat,
        title: context.state.selectedWaypoint.title,
        type: context.state.selectedWaypoint.type,
        description: context.state.selectedWaypoint.description,
        coordinateReferenceSystem: context.state.selectedWaypoint.coordinateReferenceSystem ?? 'EPSG:4326', // Common default
        public: context.state.selectedWaypoint.public,
        photos: Array.from(uniqueFinalUrls),
      };

      graph_client.request(UpdateWaypoint, { "waypoint": waypointData }).then(async (data: any) => {
        // ... (rest of saveSelectedWaypoint success/error handling) ...
        // Success:
        context.commit('resetUploadAndRemoveLists'); // Clear upload/remove tracking lists
        const element = document.getElementById('waypoint-options-dialog'); // Hide modal
        if (element) {
          const modal = Modal.getInstance(element) || new Modal(element);
          modal.hide();
        }
        await context.dispatch('refreshSelectedWaypoint'); // Get updated data including photo IDs
        if (context.state.mapContainsWaypoints) {
          await context.dispatch('addWaypointsToMap'); // Refresh map layer
        }
        // Handle errors...
      }).catch((error: any) => {
        console.error("Error saving waypoint:", error);
        alert(`Error saving waypoint: ${error.message || error}`);
      }).finally(() => {
        context.commit('setLoading', false);
      });

    }).catch(error => {
      // Catch errors from Promise.all(removalPromises) - i.e., image removal failed
      console.error("Save aborted due to image removal failure:", error);
      context.commit('setLoading', false);
      // No alert needed here, already alerted in the individual removal catch block
    });
  },

  planTrailToSelectedWaypoint(context: any) {
    if (!context.state.selectedWaypoint?.lonlat) {
      alert("No waypoint selected or waypoint has no location.");
      return;
    }
    try {
      const lngLat = context.getters.getPointFromSelectedWaypoint; // Use getter
      if (lngLat) {
        const paramValue = `${lngLat.lat},${lngLat.lng}`;
        // Ensure origin is correctly determined (consider SSR if applicable)
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        if (origin) {
          const url = `${origin}/trail-planner?startPoint=${paramValue}`;
          window.open(url, "_blank");
        } else {
          alert("Could not determine application origin URL.");
        }
      } else {
        alert("Could not parse waypoint location.");
      }
    } catch (e) {
      console.error("Error planning trail:", e);
      alert("Could not get waypoint location to plan trail.");
    }
  },

  removeWaypointImage(context: any, image: any) {
    if (!context.state.selectedWaypoint?.id || !image?.id) {
      console.warn("Cannot remove image: Missing waypoint or image ID.");
      return Promise.reject("Missing ID");
    }
    if (String(image.id).startsWith('temp_')) {
      console.log("Ignoring removal request for temporary image placeholder.");
      return Promise.resolve();
    }

    console.log(`Attempting to remove image ID: ${image.id} from waypoint ID: ${context.state.selectedWaypoint.id}`);
    return graph_client.request(DestroyWaypointImage, {
      input: {
        waypointId: context.state.selectedWaypoint.id,
        photoId: image.id // Ensure this is the correct ID type expected by backend (Int?, String?)
      }
    }).then((data: any) => {
      console.log("Image removal successful response:", data);
      return data;
    }).catch((error: any) => {
      console.error("Error removing waypoint image via API:", error);
      throw error; // Rethrow to be caught by saveSelectedWaypoint
    });
  },

  provisionallyRemoveWaypointImage(context: any, image: any) {
    if (!image) return;
    context.commit('removePhotoFromSelectedWaypoint', image);
    if (image.file?.url) {
      context.commit('removeWaypointReadyForUpload', image.file.url);
    }
    if (image.id && !String(image.id).startsWith('temp_')) {
      context.commit('addWaypointPhotoToRemove', image);
    }
  },

  importGPXFile(context: any, file: File) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const gpxContent = e.target?.result as string;
        if (!gpxContent) throw new Error("Could not read file content.");

        let geoJson = gpxFileToGeoJsonHelper(gpxContent);
        let points = geoJson.features.filter((f: any) => f.geometry?.type === "Point");

        if (points.length === 0) {
          alert("No points (wpt tags) found in the GPX file.");
          return;
        }

        if (!window.confirm(`Found ${points.length} points in GPX file. Create waypoints?`)) return;

        const bounds = getBoundsFromGeoJsonPointsFeatureCollection(geoJson);
        if (bounds && window.map?.isStyleLoaded()) {
          const convertedBounds: mapboxgl.LngLatBoundsLike = [
            [bounds.southwest[1], bounds.southwest[0]],  // [minLon, minLat]
            [bounds.northeast[1], bounds.northeast[0]]   // [maxLon, maxLat]
          ];
          window.map.fitBounds(convertedBounds, { padding: 50 });
        }

        context.commit('setLoading', true);
        context.dispatch('createWaypoints', { waypoints: points, title: file.name.replace(/\.gpx$/i, '') || 'Imported Waypoints' })
          .catch(e => {
            console.error("Error during GPX import processing:", e);
            alert("An error occurred while importing waypoints from the GPX file.");
          })
          .finally(async () => {
            await context.dispatch('addWaypointsToMap')
            context.commit('setLoading', false);
            const element = document.getElementById('waypoint-options-dialog');
            const modal = Modal.getInstance(element);
            modal?.hide();
          });

      } catch (error) {
        console.error("Error processing GPX file:", error);
        alert(`Error reading or processing GPX file: ${error instanceof Error ? error.message : error}`);
        context.commit('setLoading', false); // Ensure loading stops on error
      }
    };
    reader.onerror = (e) => {
      console.error("Error reading file:", e);
      alert("Error reading the selected file.");
    };
    reader.readAsText(file);
  },

  createWaypoints(context: any, payload: { waypoints: any[], title: string }) {
    return new Promise((resolve, reject) => {
      if (!payload.waypoints?.length) return reject("No waypoints provided.");

      const waypointsInput = payload.waypoints.map((w: any) => {
        const lon = w.geometry?.coordinates?.[0];
        const lat = w.geometry?.coordinates?.[1];
        if (lon == null || lat == null) return null;
        return {
          type: "other",
          title: w.properties?.name || w.properties?.title || "Imported Waypoint",
          description: w.properties?.desc || w.properties?.description || "",
          public: true,
          coordinateReferenceSystem: 'EPSG:4326',
          lonlat: `POINT (${lon} ${lat})`
        };
      }).filter(Boolean);

      if (waypointsInput.length === 0) return reject("No valid waypoints found.");

      const variables = { input: { input: waypointsInput } };

      graph_client.request(CreateWaypoints, variables)
        .then(async (data: any) => { // Make async to await group creation
          if (data.errors) {
            throw new Error(data.errors[0].message || "Unknown GraphQL error during CreateWaypoints");
          }

          const createdWaypoints = data.createWaypoints || [];
          console.log(`Successfully created ${createdWaypoints.length} waypoints via bulk import.`);
          if (createdWaypoints.length !== waypointsInput.length) {
            console.warn(`Only ${createdWaypoints.length} out of ${waypointsInput.length} waypoints were created successfully.`);
          }

          // Refresh map layer immediately to show new waypoints
          await context.dispatch('addWaypointsToMap');

          const waypointIds = createdWaypoints.map((w: any) => w.id).filter(Boolean);
          if (waypointIds.length > 0) {
            console.log(`Creating group "${payload.title}" for ${waypointIds.length} imported waypoints.`);
            // Await group creation before resolving the main promise
            await context.dispatch('createWaypointGroup', {
              title: payload.title,
              waypointIds: waypointIds
            });
            resolve(createdWaypoints); // Resolve with the created waypoints
          } else {
            alert("Waypoints might have been created, but none were returned successfully. No group created.");
            resolve([]); // Resolve with empty array if no IDs returned
          }
        })
        .catch((error: any) => {
          console.error("Error in createWaypoints action:", error);
          reject(`Error creating waypoints: ${error.message || error}`);
        });
    });
  },

  showWaypointOnMap(context, waypoint: any) {
    if (!waypoint?.lonlat || !waypoint?.clientId || !window.map) return;
    const map = window.map as mapboxgl.Map;
    try {
      const point = wktPointToLatLng(waypoint.lonlat);
      if (!point) return;

      map.flyTo({ center: point, zoom: 15 });

      // Clear previous single markers first
      context.dispatch('removeAllIndividualWaypointsFromMap');

      // Use the helper which adds the waypoint AND its listeners
      const poiType = POIUtility.fromString(waypoint.type);
      const addedWaypointLayerId = addWaypointToMapHelper(poiType, waypoint.clientId, point);
      if (addedWaypointLayerId) {
        context.commit('addIndividualWaypointLayerId', addedWaypointLayerId); // Track the added layer
      } else {
        console.error("showWaypointOnMap: addWaypointToMapHelper did not return a layer ID.");
      }

      // Hide modal if open
      const element = document.getElementById('waypoint-options-dialog');
      if (element) {
        const modal = Modal.getInstance(element);
        modal?.hide();
      }

    } catch(e) {
      console.error("Error showing waypoint on map:", e);
    }
  },

  exportSelectedWaypointGroupToGPX(context: any) {
    if (!context.state.selectedWaypointGroup?.waypoints?.length) {
      alert("Selected group has no waypoints to export.");
      return;
    }
    const waypoints = context.state.selectedWaypointGroup.waypoints;
    const title = context.state.selectedWaypointGroup.title || 'hiiker-waypoint-group';
    try {
      const geoJson = waypointsToGeoJsonFeatureCollectionHelper(waypoints);
      const gpxString = geojsonToGpx(geoJson, { creator: 'Hiiker App', metadata: { name: title } });
      downloadGpx(gpxString, title + ".gpx");
    } catch (e) {
      console.error("Error generating GPX for group:", e);
      alert("Failed to generate GPX file.");
    }
  },

  exportUserWaypointsToGPX(context: any) {
    if (!context.state.allUserWaypoints?.length) {
      alert("No waypoints available to export.");
      return;
    }
    const waypoints = context.state.allUserWaypoints;
    const title = 'hiiker-all-waypoints';
    try {
      const geoJson = waypointsToGeoJsonFeatureCollectionHelper(waypoints);
      const gpxString = geojsonToGpx(geoJson, { creator: 'Hiiker App', metadata: { name: title } });
      downloadGpx(gpxString, title + ".gpx");
    } catch (e) {
      console.error("Error generating GPX for all waypoints:", e);
      alert("Failed to generate GPX file.");
    }
  },

  exportWaypointToGPX(context: any, waypoint: any) {
    if (!waypoint) {
      alert("No waypoint provided for export.");
      return;
    }
    const title = waypoint.title || 'hiiker-waypoint';
    try {
      const geoJson = waypointsToGeoJsonFeatureCollectionHelper([waypoint]); // Pass as array
      const gpxString = geojsonToGpx(geoJson, { creator: 'Hiiker App', metadata: { name: title } });
      downloadGpx(gpxString, title + ".gpx");
    } catch (e) {
      console.error("Error generating GPX for waypoint:", e);
      alert("Failed to generate GPX file.");
    }
  },

  exportWaypointGroupToGPX(context: any, waypointGroup: any) {
    if (!waypointGroup?.waypoints?.length) {
      alert("No waypoints in the selected group to export.");
      return;
    }
    const waypoints = waypointGroup.waypoints;
    const title = waypointGroup.title || 'hiiker-waypoint-group';
    try {
      const geoJson = waypointsToGeoJsonFeatureCollectionHelper(waypoints);
      const gpxString = geojsonToGpx(geoJson, { creator: 'Hiiker App', metadata: { name: title } });
      downloadGpx(gpxString, title + ".gpx");
    } catch (e) {
      console.error("Error generating GPX for waypoint group:", e);
      alert("Failed to generate GPX file.");
    }
  },

  destroySelectedWaypointGroup(context: any) {
    if (!context.state.selectedWaypointGroup?.id) {
      alert("No waypoint group selected.");
      return;
    }
    if (!confirm(`Are you sure you want to delete group "${context.state.selectedWaypointGroup.title || 'this group'}"?`)) {
      return;
    }
    const groupId = context.state.selectedWaypointGroup.id;
    context.commit('setLoading', true);
    graph_client.request(DestroyWaypointGroup, { waypointGroup: { id: groupId } }).then(async (data: any) => {
      console.log("Destroy group response:", data);
      await context.dispatch('getAllUserWaypointGroups'); // Refresh list
      context.commit('setSelectedWaypointGroup', null); // Clear selection
      context.commit('popPage'); // Go back in UI if applicable
    }).catch(error => {
      console.error("Error destroying waypoint group:", error);
      alert("Failed to delete waypoint group.");
    }).finally(() => {
      context.commit('setLoading', false);
    });
  }
}; // End of actions

// Helper function for downloading GPX
function downloadGpx(gpxString: string, filename: string) {
  try {
    const blob = new Blob([gpxString], { type: 'application/gpx+xml;charset=utf-8' }); // Specify charset
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.gpx') ? filename : `${filename}.gpx`; // Ensure .gpx extension
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up blob URL immediately
  } catch (error) {
    console.error("Error in downloadGpx:", error);
    alert("Could not create GPX download link.");
  }
}


const mutations: MutationTree<WaypointDialogState> = {
  // Loading state
  setLoading(state: WaypointDialogState, isLoading: boolean) {
    state.loading = isLoading;
  },

  // Page stack
  setTopPage(state: WaypointDialogState, page: WaypointOptionsDialogPages) {
    if (state.pageStack[state.pageStack.length - 1] !== page) {
      state.pageStack.push(page);
    }
  },
  popPage(state: WaypointDialogState) {
    if (state.pageStack.length > 1) {
      state.pageStack.pop();
    }
  },
  resetPageStack(state: WaypointDialogState) {
    state.pageStack = [WaypointOptionsDialogPages.WaypointOptions];
  },

  // Waypoint Adding Mode
  toggleWaypointAddingNotification(state: WaypointDialogState) {
    state.waypointAddingModeEnabled = !state.waypointAddingModeEnabled;
  },
  setWaypointAddingMode(state: WaypointDialogState, enabled: boolean) {
    state.waypointAddingModeEnabled = enabled;
  },

  // Waypoint Lists
  setAllUserWaypoints(state: WaypointDialogState, waypoints: any[]) {
    state.allUserWaypoints = waypoints;
  },
  setSearchedWaypoints(state: WaypointDialogState, waypoints: any[]) {
    state.searchedWaypoints = waypoints;
  },
  clearSearchedWaypoints(state: WaypointDialogState) {
    state.searchedWaypoints = [];
  },

  // Waypoint Groups
  setAllUserWaypointGroups(state: WaypointDialogState, groups: any[]) {
    state.allUserWaypointGroups = groups;
  },
  setSelectedWaypointGroup(state: WaypointDialogState, group: any) {
    state.selectedWaypointGroup = group;
  },

  // Selected Waypoint
  setSelectedWaypoint(state: WaypointDialogState, waypoint: any) {
    state.selectedWaypoint = waypoint;
    // Reset related temporary states when a new waypoint is selected/cleared
    if (waypoint === null) {
      state.waypointsReadyForUpload = [];
      state.waypointPhotosAwaitingUpload = [];
      state.waypointPhotosToRemove = [];
    }
  },
  // Mutations for specific fields of the selected waypoint (ensure selectedWaypoint exists)
  setSelectedWaypointDescription(state, description) {
    if (state.selectedWaypoint) state.selectedWaypoint.description = description;
  },
  setSelectedWaypointTitle(state, title) {
    if (state.selectedWaypoint) state.selectedWaypoint.title = title;
  },
  setSelectedWaypointType(state, type) {
    if (state.selectedWaypoint) state.selectedWaypoint.type = type;
  },
  setSelectedWaypointPublic(state, isPublic) {
    if (state.selectedWaypoint) state.selectedWaypoint.public = isPublic;
  },
  // For optimistic UI updates with photos
  addPhotoToSelectedWaypoint(state, photo) {
    if (state.selectedWaypoint) {
      // Initialize array if it doesn't exist
      if (!state.selectedWaypoint.waypointPhotos) {
        state.selectedWaypoint.waypointPhotos = [];
      }
      state.selectedWaypoint.waypointPhotos.push(photo);
    }
  },
  removePhotoFromSelectedWaypoint(state, photoToRemove) {
    if (state.selectedWaypoint?.waypointPhotos) {
      state.selectedWaypoint.waypointPhotos = state.selectedWaypoint.waypointPhotos.filter((photo: any) => {
        return photo.id ? photo.id !== photoToRemove.id : photo.file?.url !== photoToRemove.file?.url;
      });
    }
  },

  // Photo Upload/Removal Tracking
  addWaypointPhotosAwaitingUpload(state, files: File[]) {
    state.waypointPhotosAwaitingUpload = [...state.waypointPhotosAwaitingUpload, ...files];
  },
  removeWaypointPhotoAwaitingUpload(state, file: File) {
    state.waypointPhotosAwaitingUpload = state.waypointPhotosAwaitingUpload.filter(f => f !== file);
  },
  addWaypointReadyForUpload(state, url: string) {
    // Avoid duplicates if somehow added multiple times
    if (!state.waypointsReadyForUpload.includes(url)) {
      state.waypointsReadyForUpload.push(url);
    }
  },
  removeWaypointReadyForUpload(state, urlToRemove: string) {
    state.waypointsReadyForUpload = state.waypointsReadyForUpload.filter(url => url !== urlToRemove);
  },
  addWaypointPhotoToRemove(state, photo: any) {
    // Avoid adding duplicates
    if (!state.waypointPhotosToRemove.some(p => p.id === photo.id)) {
      state.waypointPhotosToRemove.push(photo);
    }
  },
  clearWaypointPhotosToRemove(state) {
    state.waypointPhotosToRemove = [];
  },
  resetUploadAndRemoveLists(state) {
    state.waypointsReadyForUpload = [];
    state.waypointPhotosAwaitingUpload = [];
    state.waypointPhotosToRemove = [];
  },

  // Map State
  setMapContainsWaypoints(state: WaypointDialogState, containsWaypoints: boolean) {
    state.mapContainsWaypoints = containsWaypoints;
  },
  addIndividualWaypointLayerId(state: WaypointDialogState, layerId: string) {
    // Avoid duplicates if helper is called unexpectedly multiple times
    if (!state.individualWaypointLayerIds.includes(layerId)) {
      state.individualWaypointLayerIds.push(layerId);
    }
  },
  clearIndividualWaypointLayerIds(state: WaypointDialogState) {
    state.individualWaypointLayerIds = [];
  },
}; // End of mutations


const getters: GetterTree<WaypointDialogState, RootState> = {
  topPage(state) {
    // Ensure pageStack is never empty (should always have at least the base page)
    return state.pageStack[state.pageStack.length - 1] || WaypointOptionsDialogPages.WaypointOptions;
  },
  // Page Checkers
  topPageIsWaypointOptions: (state, getters) => getters.topPage === WaypointOptionsDialogPages.WaypointOptions,
  topPageIsWaypointViewDetails: (state, getters) => getters.topPage === WaypointOptionsDialogPages.WaypointViewDetails,
  topPageIsWaypointEditDetails: (state, getters) => getters.topPage === WaypointOptionsDialogPages.WaypointEditDetails,
  topPageIsWaypoints: (state, getters) => getters.topPage === WaypointOptionsDialogPages.Waypoints,
  topPageIsWaypointGroups: (state, getters) => getters.topPage === WaypointOptionsDialogPages.WaypointGroups,
  topPageIsWaypointGroupsWaypoints: (state, getters) => getters.topPage === WaypointOptionsDialogPages.WaypointGroupsWaypoints,
  topPageIsWaypointDetails: (state, getters) => getters.topPage === WaypointOptionsDialogPages.WaypointDetails, // Note: Is this different from View/Edit?
  topPageIsWaypointGroupSelection: (state, getters) => getters.topPage === WaypointOptionsDialogPages.WaypointGroupSelection,

  // Data getters
  waypointsForList(state) {
    // Prioritize searched waypoints if available, otherwise show all
    return state.searchedWaypoints.length > 0 ? state.searchedWaypoints : state.allUserWaypoints;
  },
  selectedWaypointShareUrl(state) {
    if (!state.selectedWaypoint?.clientId) return null;
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      return origin ? `${origin}/map?waypoint=${state.selectedWaypoint.clientId}` : null;
    } catch (e) {
      console.error("Could not generate share URL", e);
      return null;
    }
  },
  getPointFromSelectedWaypoint(state): mapboxgl.LngLat | null {
    if (!state.selectedWaypoint?.lonlat) return null;
    try {
      return wktPointToLatLng(state.selectedWaypoint.lonlat);
    } catch (e) {
      console.error("Error parsing WKT point:", e);
      return null;
    }
  },
  isLoading(state): boolean {
    return state.loading;
  },
  photosAwaitingUploadCount(state): number {
    return state.waypointPhotosAwaitingUpload.length;
  }
}; // End of getters

// Define the module using the Vuex 4 object structure
const waypointDialogModule: Module<WaypointDialogState, RootState> = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};

export default waypointDialogModule;

// Global type declarations (ensure these match your actual environment/types)
declare global {
  interface Window {
    map: mapboxgl.Map;
    addMarkerClickedFromPopup?: (lngLat: mapboxgl.LngLatLike) => void;
  }
  // Basic jQuery type stub if not using full @types/jquery or if $ is globally available
  interface JQuery {
    modal(action: string | object): JQuery; // Allow 'hide', 'show', options object etc.
    on(event: string, handler: (...args: any[]) => void): JQuery;
  }
  function $(selector: string | Element): JQuery; // Allow selecting by element
}

// Re-declarations of enums/models if they are not imported correctly or globally available
/*
export enum WaypointOptionsDialogPages { ... }
export enum POI { ... }
export const POIUtility = { ... }
*/
